/**
 * Initializes an Alpine.js data object to manage the interactive elements of a product card, such as displaying product information, managing a quick add feature, and handling responsive behaviors.
 * Key features include:
 * 1. Product Data Retrieval: Fetches and displays detailed product data, including images, descriptions, and pricing.
 * 2. Dynamic Carousel: Initializes a Swiper carousel for product images on devices with a width less than 1024 pixels.
 * 3. Price Formatting: Provides multiple pricing formats including a simplified price without decimals, a minimum price, and a comparative price showing discounts.
 * 4. Product Badges: Dynamically generates badges based on product data like promotions or stock status.
 * 5. Quick Add Functionality: Enables quick adding of products to the cart with detailed handling of variant selection, especially useful on mobile devices or for products with multiple variants.
 * 6. Error Handling: Manages loading errors and updates the UI accordingly to inform the user.
 * 7. Responsive Adjustments: Adjusts UI elements and functionality based on the screen size and context (e.g., different behavior inside upsell sections vs. regular product listings).
 * 8. Event Management: Sets up click events for adding products to the cart and toggling additional information like reviews or detailed product descriptions.
*/

const productCard = (productId) => ({
  loading: false,
  error: '',
  productId,
  quickAddOpen: false,
  hover: false,
  initCarousel: function () {
    this.$nextTick(() => {
      if (window.innerWidth < 1024 && typeof Swiper !== 'undefined') {
        new Swiper(`.swiper-container-plp-card-${this.shopifyData.handle}`, {
          slidesPerView: 'auto',
          spaceBetween: 10,
          pagination: {
            el: `.swiper-pagination-plp-card-${this.shopifyData.handle}`,
            type: 'progressbar',
          },
        });
      }
    });
  },

  get shopifyData() {
    if (!this.productId) return null;
    if (this.loading) return null;
    return productsData[this.productId];
  },

  get collectionText() {
    if (this.shopifyData?.productType === 'Hanging Chair Hammock') {
      return 'Chair Hammock';
    } else if (this.shopifyData?.productType === 'Cotton Rope Hammock') {
      return this.shopifyData?.productType;
    } else if (this.shopifyData?.productType === 'Classic Double Hammock') {
      return 'Signature Hammock';
    } else return null;
  },

  get link() {
    if (!this.shopifyData) return '/';
    return `${window.Shopify.routes.root}products/${this.shopifyData.handle}`;
  },

  get images() {
    return (this.shopifyData?.images || [])
      .filter((image) => {
        // Ensure image and image.originalSrc are defined
        if (!image || !image.originalSrc) return false;

        // Modify the regex to ignore query parameters or fragments
        return /\.(jpeg|jpg|png)(\?.*)?(\#.*)?$/i.test(image.originalSrc);
      })
      .slice(0, 4);
  },

  get description() {
    const desc = this.shopifyData.description;
    if (desc && desc.length > 55) {
      return desc.substring(0, 55) + '...';
    }
    return desc;
  },

  get productReviewCount() {
    const rating = this.shopifyData.ratingCount?.value;
    const ratingNormalized = Number(rating) || 0;
    return `${ratingNormalized} Review${ratingNormalized === 1 ? '' : 's'}`;
  },

  get qualifiersParsed() {
    const { qualifiers } = this.shopifyData;
    if (!qualifiers?.value) return '';
    try {
      return JSON.parse(qualifiers.value).join(' ');
    } catch (e) {
      return '';
    }
  },

  get title() {
    const { description } = this.shopifyData;
    if (description?.value) return description.value;

    return `${this.qualifiersParsed} ${this.shopifyData.type.value}`;
  },

  get shortenedTitle() {
    const { enhancedTitle } = this.shopifyData;
    if (enhancedTitle && enhancedTitle.length > 0) {
      const ind = enhancedTitle.indexOf('/');
      return enhancedTitle.substring(0, ind - 1);
    } else return null;
  },

  get legacyTitle() {
    return this.shopifyData.title;
  },

  get price() {
    return this.shopifyData.priceRangeFormatted;
  },

  get minPrice() {
    minVarPrice = this.shopifyData.priceRangeFormatted.minVariantPrice;
    minVarPriceFormatted = formatPrice(minVarPrice.currencyCode, minVarPrice.amount);
    removedDecimalPrice = simplePrice(minVarPriceFormatted);
    return removedDecimalPrice;
  },

  get maxPrice() {
     const variants = this.shopifyData.variants;
     return simplePrice(variants[0].formattedPrice);
  },

  get maxCPrice() {
     const variants = this.shopifyData.variants;
     return simplePrice(variants[0].formattedCompareAtPrice);
  },

  get compareAtPrice() {
    // return JSON.stringify(this.currentProduct.compareAtPriceRangeFormatted);
    let compareVarPrice = this.shopifyData.compareAtPriceRangeFormatted.maxVariantPrice;
    let compareVarPriceFormatted = formatPrice(compareVarPrice.currencyCode, compareVarPrice.amount);
    let removedDecimalPrice = simplePrice(compareVarPriceFormatted);
    if(parseInt(compareVarPrice.amount) > 0) {
      return removedDecimalPrice;
    } else return '';
  },

  get minComparePrice() {
    minVarPrice = this.shopifyData.compareAtPriceRangeFormatted.minVariantPrice;
    minVarPriceFormatted = formatPrice(minVarPrice.currencyCode, minVarPrice.amount);
    removedDecimalPrice = simplePrice(minVarPriceFormatted);
    return removedDecimalPrice;
  },

  get simpleFormattedPrice() {
    return simplePrice(this.price);
  },

  get secondVariantPrice() {
    const variants = this.shopifyData.variants;
    if (variants.length > 1) {
      return simplePrice(variants[1].formattedPrice);
    }
    return simplePrice(variants[0].formattedPrice);
  },

  get reviewSnippet() {
    return this.shopifyData.reviewSnippet;
  },

  get priceRegular() {
    return this.shopifyData.compareAtPriceRangeFormatted;
  },

  get hasReducedPrice() {
    const hammockPr = window.HAMMOCK_PRODUCT;
    const { priceRegular, price } = this;
    if(this.shopifyData.handle == hammockPr){
      return true;
    }else{
      return priceRegular.minVariantPrice.amount > price.minVariantPrice.amount;
    } 
  },

  get pricingText() {
    return `reduced price: ${this.price}, regular price: ${this.priceRegular}`;
  },

  get badges() {
    return this.shopifyData ? getProductBadges(this.shopifyData) : [];
  },

  get pricingExtraText() {
    const { tags } = this.shopifyData;
    const tag = tags.find((tag) => tag.startsWith('Off:'));
    if (!tag) return '';
    const pct = tag.replace('Off:', '');
    return `Extra ${pct}% off on Checkout`;
  },

  get quickAddTitle() {
    const { shopifyData } = this;

    if (shopifyData.availableForSale) {
      return 'Quick Add';
    }

    if (shopifyData.tags.includes('Coming Soon') || shopifyData.seasonalCore?.value === 'Core') {
      return 'Notify Me';
    }

    return 'Sold Out';
  },

  get isSoldOut() {
    return this.quickAddTitle === 'Sold Out';
  },

  init() {
    if (!this.shopifyData) {
      this.loading = true;
      fetchProductCard([this.productId])
        .then(() => {
          this.loading = false;
        })
        .catch((e) => {
          this.error = `Error: ${e.message || 'Unknown error'}`;
        });
    }
  },

  handleQuickAdd() {
    if (this.$root.closest('.cart__upsells') === null) {
      if (window.innerWidth < 1024) {
        this.$dispatch('open-quick-add-drawer', {
          product: this.shopifyData,
          selectSecondVariant: this.displaySecondVariant || null,
        });
      } else {
        this.quickAddOpen = true;
      }
    } else {
      this.$dispatch('open-quick-add-drawer', {
        product: this.shopifyData,
        selectSecondVariant: this.displaySecondVariant || null,
      });
    }
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productCard', productCard);
});
